/**
 * Shape samplers for the homepage scroll story.
 *
 * Each sampler returns a list of `{x, y, c}` points in the coordinate
 * space of the heading canvas — i.e. top-left corner is (0, 0), width
 * and height are the heading's own union rect. This means the shapes
 * naturally sit BEHIND the heading area, and the parent doesn't need
 * to re-align anything.
 *
 * We sample cheap analytic shapes (circles, rectangles, rotated lines)
 * on a small grid so the total point count is roughly the same order
 * as the heading itself — the master dot list in ParticleHeading maps
 * one-to-one via index. Colours are baked into each point so the
 * component doesn't need to know anything about palettes.
 */

const CYAN = "0,240,255";
const WHITE = "255,255,255";
const GREEN = "57,255,20";
const GOLD = "245,196,0";

// Draw a filled shape into an offscreen canvas at the same {W, H}
// bounds as the heading, then sample on a 3px grid. The colour is
// baked in via `fillStyle`. Alpha threshold matches the heading.
function _rasterize(W, H, drawFn) {
  const off = document.createElement("canvas");
  off.width = W;
  off.height = H;
  const ctx = off.getContext("2d", { willReadFrequently: true });
  if (!ctx) return [];
  drawFn(ctx);
  const data = ctx.getImageData(0, 0, W, H).data;
  const points = [];
  for (let y = 0; y < H; y += 3) {
    for (let x = 0; x < W; x += 3) {
      const idx = (y * W + x) * 4;
      const a = data[idx + 3];
      if (a > 80) {
        // Read colour back so callers can override on a per-region basis
        const r = data[idx], g = data[idx + 1], b = data[idx + 2];
        points.push({ x, y, c: `${r},${g},${b}` });
      }
    }
  }
  return points;
}

/**
 * Radar shape — three concentric rings, a diagonal sweep line, ten
 * blips (three green as "just started promoting"), a cross-hair at
 * centre. Sized to fill roughly the same square area as the growth
 * chart, so both stages feel like siblings.
 *
 * Ring strokes are 6px so 3px-grid sampling always catches them; the
 * outermost ring is at 90 % of the shorter axis so it's big and
 * unmistakable. The sweep wedge is drawn at −22° (up-right) with a
 * radial linear gradient (falling off from centre) rendered here as a
 * simple wide stroke — the ParticleHeading doesn't per-frame rotate;
 * a static tilt reads as "radar sweep frozen at this instant" and
 * avoids two competing motions during the dot morph.
 */
export function radarSampler({ W, H }) {
  const cx = W / 2;
  const cy = H / 2;
  const rMax = Math.min(W, H) * 0.42;
  const rings = [rMax * 0.35, rMax * 0.7, rMax];

  const points = _rasterize(W, H, (ctx) => {
    // Rings — cyan, thick enough that 3px sampling always catches them.
    ctx.strokeStyle = `rgb(${CYAN})`;
    ctx.lineWidth = 6;
    rings.forEach((r) => {
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.stroke();
    });
    // Sweep wedge — a bright leading line plus a trailing softer arc
    // for the "radar just swept through here" feel. Baked static.
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(-Math.PI / 8); // ~22.5° up-right
    ctx.strokeStyle = `rgb(${CYAN})`;
    ctx.lineWidth = 8;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(rMax, 0);
    ctx.stroke();
    // Trailing wedge — thinner line at a small angle for depth.
    ctx.rotate(0.35);
    ctx.lineWidth = 5;
    ctx.strokeStyle = `rgb(${CYAN})`;
    ctx.globalAlpha = 0.55;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(rMax * 0.85, 0);
    ctx.stroke();
    ctx.globalAlpha = 1;
    ctx.restore();
    // Cross-hair — square 12px marker at centre.
    ctx.fillStyle = `rgb(${CYAN})`;
    ctx.fillRect(cx - 2, cy - 12, 4, 24);
    ctx.fillRect(cx - 12, cy - 2, 24, 4);
  });

  // Blips — twelve hand-placed constellation. Each blip is a filled
  // 12×12 square (rasterises to ~9 dots at 3px grid) with a small
  // fainter halo for depth.
  const blips = [
    { a: 0.35, r: 0.55, c: WHITE, big: false },
    { a: 1.05, r: 0.88, c: GREEN, big: true  },
    { a: 1.85, r: 0.42, c: CYAN,  big: false },
    { a: 2.4,  r: 0.75, c: WHITE, big: false },
    { a: 3.15, r: 0.95, c: GREEN, big: true  },
    { a: 3.7,  r: 0.55, c: WHITE, big: false },
    { a: 4.35, r: 0.82, c: CYAN,  big: false },
    { a: 4.95, r: 0.68, c: GREEN, big: true  },
    { a: 5.55, r: 0.45, c: WHITE, big: false },
    { a: 6.05, r: 0.9,  c: CYAN,  big: false },
    { a: 0.75, r: 0.72, c: WHITE, big: false },
    { a: 2.85, r: 0.55, c: CYAN,  big: false },
  ];
  blips.forEach((b) => {
    const bx = cx + Math.cos(b.a) * rMax * b.r;
    const by = cy + Math.sin(b.a) * rMax * b.r;
    const spread = b.big ? 9 : 6;
    for (let dy = -spread; dy <= spread; dy += 3) {
      for (let dx = -spread; dx <= spread; dx += 3) {
        points.push({ x: Math.round(bx + dx), y: Math.round(by + dy), c: b.c });
      }
    }
  });

  return points;
}

/**
 * Growth-chart shape — a row of bars of increasing height, drawn along
 * the bottom of the canvas. The rightmost bar is green (the "win").
 * Bars use rounded tops (2px radius) to feel bespoke rather than "AI
 * dashboard mockup".
 */
export function chartSampler({ W, H }) {
  const BAR_COUNT = 8;
  const gutter = W * 0.05;
  const usableW = W - gutter * 2;
  const barW = (usableW / BAR_COUNT) * 0.72;
  const step = usableW / BAR_COUNT;
  const baseY = H * 0.92;
  const maxBarH = H * 0.85;

  const points = _rasterize(W, H, (ctx) => {
    // Baseline axis — thick enough that 3px sampling always catches it.
    ctx.strokeStyle = `rgb(${CYAN})`;
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(gutter * 0.5, baseY);
    ctx.lineTo(W - gutter * 0.5, baseY);
    ctx.stroke();

    for (let i = 0; i < BAR_COUNT; i++) {
      const t = i / (BAR_COUNT - 1);
      const h = maxBarH * (0.18 + 0.82 * Math.pow(t, 1.4));
      const x = gutter + step * i + (step - barW) / 2;
      const y = baseY - h;
      ctx.fillStyle = i === BAR_COUNT - 1 ? `rgb(${GREEN})` : `rgb(${CYAN})`;
      ctx.beginPath();
      ctx.moveTo(x, baseY);
      ctx.lineTo(x, y + 3);
      ctx.quadraticCurveTo(x, y, x + 3, y);
      ctx.lineTo(x + barW - 3, y);
      ctx.quadraticCurveTo(x + barW, y, x + barW, y + 3);
      ctx.lineTo(x + barW, baseY);
      ctx.closePath();
      ctx.fill();
    }
  });

  return points;
}
chartSampler.orderBy = "x";
